import React, { createContext, useContext, useState, useEffect } from "react";
import { pb } from "../services/pocketbase";

const AuthContext = createContext({});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // --- HELPER DE PARSER ---
  const formatAccessArray = (rawAccess) => {
    // 1. Se vier null, undefined ou vazio
    if (!rawAccess) return [];
    
    // 2. Se já for um Array
    if (Array.isArray(rawAccess)) {
      // O PocketBase às vezes aninha arrays JSON: [["stock", "orders"]]
      if (rawAccess.length === 1 && Array.isArray(rawAccess[0])) {
        return rawAccess[0]; 
      }
      return rawAccess;
    }
    
    // 3. Se for String (pode ser JSON encodado duplo ou virgulas)
    if (typeof rawAccess === 'string') {
      try {
        const parsed = JSON.parse(rawAccess);
        if (Array.isArray(parsed)) {
            if (parsed.length === 1 && Array.isArray(parsed[0])) {
                return parsed[0];
            }
            return parsed;
        }
        return [];
      } catch (e) {
        return rawAccess.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    
    return [];
  };

  // --- RESTAURAR SESSÃO ---
  useEffect(() => {
    const restoreSession = async () => {
      if (pb.authStore.isValid) {
        try {
          // Faz refresh silencioso com o servidor para validar o token atual
          const authData = await pb.collection("users").authRefresh();
          const model = authData.record;
          const currentUser = {
            id: model.id,
            name: model.name,
            username: model.username,
            role: model.role || "user",
            access: formatAccessArray(model.access || model.acess),
            avatar: model.avatar || "",
            collectionId: model.collectionId || "users",
            created: model.created,
          };
          
          console.log("[AuthContext] Sessão Restaurada e Validada. Access Array:", currentUser.access);
          setUser(currentUser);
        } catch (e) {
          console.warn("[AuthContext] Falha ao revalidar sessão no carregamento (token inválido/expirado):", e);
          pb.authStore.clear();
          setUser(null);
        }
      }
      setLoading(false);
    };

    restoreSession();

    // Escuta evento customizado de não-autorizado (erro 401 do PocketBase)
    const handleUnauthorized = () => {
      console.warn("[AuthContext] Evento pb-unauthorized recebido. Deslogando usuário...");
      setUser(null);
      // Força um reload limpo para zerar todos os estados locais e redirecionar para a tela de login
      window.location.reload();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("pb-unauthorized", handleUnauthorized);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("pb-unauthorized", handleUnauthorized);
      }
    };
  }, []);

  // --- FUNÇÃO DE LOGIN ---
  const login = async (username, password) => {
    try {
      if (!username || !password) {
        return { success: false, message: "Preencha todos os campos." };
      }

      // Garante que espaços extras não causem erro 400
      const cleanUsername = username.trim();
      const cleanPassword = password.trim();

      // --------------------------
      // PocketBase Login (Usuário + Senha)
      // O PB valida e guarda o token automaticamente no authStore
      const authData = await pb.collection("users").authWithPassword(cleanUsername, cleanPassword);

      const userData = authData.record;

      // MONTA O OBJETO DE SESSÃO DO REACT
      const sessionUser = {
        id: userData.id,
        name: userData.name,
        username: userData.username,
        role: userData.role || "user",
        // Aqui também garante
        access: formatAccessArray(userData.access || userData.acess), 
        avatar: userData.avatar || "",
        collectionId: userData.collectionId || "users",
        created: userData.created,
      };

      console.log("[AuthContext] Login Sucesso. Access Array:", sessionUser.access);
      setUser(sessionUser);
      return { success: true };

    } catch (error) {
      console.error("Erro no login:", error);
      // Melhora a mensagem de erro se possível
      let msg = "Usuário ou senha incorretos.";
      if (error?.data?.message) msg = error.data.message;
      return { success: false, message: msg };
    }
  };

  // --- FUNÇÃO DE LOGOUT ---
  const logout = () => {
    pb.authStore.clear(); // Limpa token do PB
    setUser(null);
    window.location.reload();
  };

  // --- VERIFICAÇÃO DE PERMISSÃO ---
  const hasAccess = (moduleKey) => {
    if (!user) return false;

    // 1. Se for MASTER, tem acesso a tudo.
    if (user.role === "master") return true;

    // 2. Se a role for "motoboy", concede acesso automático ao módulo "motoboy"
    if (user.role === "motoboy" && moduleKey === "motoboy") return true;

    // 3. Se o array de acesso não existir, bloqueia
    if (!Array.isArray(user.access)) return false;

    // 4. Verifica se a chave da aba está no array
    return user.access.includes(moduleKey);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasAccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
